import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  Divider,
} from '@mui/material';
import {
  Article,
  CloudUpload,
  Videocam,
  MenuBook,
  Public,
  Lock,
  LocalOffer,
  School,
  Person,
  ErrorOutline,
} from '@mui/icons-material';
import postsService from '../../../services/PostsService';
import teacherService from '../../../services/TeacherService';
import { getVideos } from '../../../services/VideosService';
import documentsService from '../../../services/DocumentsService';
import labelsService from '../../../services/LabelsService';
import coursesService from '../../../services/CoursesService';
import { stripHtml } from '../../../utils/stingUtils';

const BRAND = '#4DABA4';
const BRAND_DARK = '#2f6f6a';

const courseOptionLabel = (option) =>
  stripHtml(option?.title) || `Curso ${option?.id ?? ''}`;

const courseOptionSx = {
  '& p': { m: 0, display: 'inline' },
  '& strong, & b': { fontWeight: 700 },
  '& em, & i': { fontStyle: 'italic' },
  lineHeight: 1.35,
};

const extractApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return 'Error al guardar la publicación';
  if (typeof data === 'string' && data.trim()) return data;

  const validationMessages = data.errors
    ? Object.values(data.errors).flat().filter(Boolean)
    : [];
  if (validationMessages.length) return validationMessages.join(' · ');

  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (typeof data.title === 'string' && data.title.trim()) return data.title;
  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;

  return 'Error al guardar la publicación';
};

const apiErrorMentionsLabels = (error) => {
  const data = error?.response?.data;
  const blob = [
    typeof data === 'string' ? data : '',
    data?.message,
    data?.title,
    data?.detail,
    data?.errors ? JSON.stringify(data.errors) : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return blob.includes('etiqueta') || blob.includes('label');
};

const getLabelId = (label) => {
  const raw = label?.id ?? label?.Id;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const normalizeLabel = (label) => {
  if (!label) return null;
  const id = getLabelId(label);
  if (id == null) return null;
  return {
    ...label,
    id,
    name: label.name || label.Name || `Etiqueta ${id}`,
  };
};

const CategoryCard = ({ active, icon: Icon, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'flex flex-1 items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all duration-200',
      active
        ? 'border-[#4DABA4] bg-[#4DABA4]/10 shadow-md shadow-[#4DABA4]/15'
        : 'border-slate-200 bg-white hover:border-[#4DABA4]/50 hover:bg-[#4DABA4]/5',
    ].join(' ')}
  >
    <span
      className={[
        'inline-flex h-11 w-11 items-center justify-center rounded-xl',
        active ? 'bg-[#4DABA4] text-white' : 'bg-slate-100 text-[#4DABA4]',
      ].join(' ')}
    >
      <Icon />
    </span>
    <span>
      <span className="block text-sm font-bold text-slate-800">{title}</span>
      <span className="mt-0.5 block text-xs text-slate-500">{subtitle}</span>
    </span>
  </button>
);

const Section = ({ step, title, hint, children }) => (
  <Box className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    <Box className="mb-4 flex items-start gap-3">
      <Box
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        sx={{ bgcolor: BRAND }}
      >
        {step}
      </Box>
      <Box>
        <Typography className="text-sm font-bold text-slate-800">{title}</Typography>
        {hint && (
          <Typography className="mt-0.5 text-xs text-slate-500">{hint}</Typography>
        )}
      </Box>
    </Box>
    {children}
  </Box>
);

const PostForm = ({ initialData, onSuccess, onCancel, hideTitle = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail: null,
    thumbnailPreview: null,
    category: '',
    teacherId: null,
    videoId: null,
    documentId: null,
    courseId: null,
    labelIds: [],
    isPublic: false,
  });

  const [teachers, setTeachers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [labels, setLabels] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [formAlert, setFormAlert] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const formAlertRef = useRef(null);
  const labelsFieldRef = useRef(null);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchFormData();
  }, []);

  useEffect(() => {
    return () => {
      if (formData.thumbnailPreview?.startsWith?.('blob:')) {
        URL.revokeObjectURL(formData.thumbnailPreview);
      }
    };
  }, [formData.thumbnailPreview]);

  useEffect(() => {
    if (initialData && !isLoadingData) {
      populateFormData();
    }
  }, [initialData, isLoadingData, teachers, videos, documents, labels, courses]);

  const fetchFormData = async () => {
    try {
      setIsLoadingData(true);
      const [teachersData, videosData, documentsData, labelsData, coursesData] = await Promise.all([
        teacherService.getTeachers(),
        getVideos(),
        documentsService.getDocuments(),
        labelsService.getAllLabels(),
        coursesService.getCourses().catch(() => []),
      ]);

      setTeachers(teachersData || []);
      setVideos(videosData || []);
      setDocuments(documentsData || []);
      const normalizedLabels = (Array.isArray(labelsData) ? labelsData : [])
        .map(normalizeLabel)
        .filter(Boolean);
      setLabels(normalizedLabels);
      setCourses(coursesService.normalizeCoursesList(coursesData) || []);
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
      showToast('Error al cargar los datos necesarios', 'error');
    } finally {
      setIsLoadingData(false);
    }
  };

  const findTeacherId = (teacherName) => {
    if (!teacherName) return null;
    const teacher = teachers.find((t) => t.name === teacherName);
    return teacher ? teacher.id : null;
  };

  const mapLabelIds = (source) => {
    if (Array.isArray(source?.labelIds) && source.labelIds.length) {
      return source.labelIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    if (!source?.labels) return [];
    return source.labels
      .map((label) => {
        const found = labels.find(
          (l) =>
            l.name === (label.name || label.Name)
            || Number(l.id) === Number(label.id ?? label.Id)
        );
        return found ? found.id : getLabelId(label);
      })
      .filter((id) => id != null);
  };

  const applyPostToForm = (post) => {
    const rawThumb = post.thumbnail || post.Thumbnail || '';
    setFormData({
      title: post.title || '',
      description: post.description || '',
      thumbnail: null,
      thumbnailPreview: postsService.resolveMediaUrl(rawThumb) || rawThumb || null,
      category: post.category || '',
      teacherId: post.teacherId || findTeacherId(post.teacherName) || null,
      videoId: post.videoId || null,
      documentId: post.documentId || null,
      courseId: post.courseId || post.CourseId || null,
      isPublic: Boolean(post.isPublic ?? post.IsPublic ?? false),
      labelIds: mapLabelIds(post),
    });
  };

  const populateFormData = () => {
    if (!initialData) return;

    const needsFull =
      initialData.id &&
      !initialData.teacherId &&
      !initialData.videoId &&
      !initialData.documentId;

    if (needsFull) {
      postsService
        .getPostById(initialData.id)
        .then(applyPostToForm)
        .catch((error) => {
          console.error('Error al obtener el post completo:', error);
          applyPostToForm(initialData);
        });
      return;
    }

    applyPostToForm(initialData);
  };

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const showFormError = (message, nextFieldErrors = {}) => {
    setFormAlert(message);
    setFieldErrors(nextFieldErrors);
    showToast(message, 'error');
    requestAnimationFrame(() => {
      formAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (nextFieldErrors.labels) {
        labelsFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const clearFormError = () => {
    setFormAlert(null);
    setFieldErrors({});
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'labelIds') {
      setFieldErrors((prev) => {
        if (!prev.labels) return prev;
        const next = { ...prev };
        delete next.labels;
        return next;
      });
      if (Array.isArray(value) && value.length > 0) {
        setFormAlert((prev) =>
          prev && String(prev).toLowerCase().includes('etiqueta') ? null : prev
        );
      }
    }
  };

  const handleCategoryChange = (newCategory) => {
    setFormData((prev) => ({
      ...prev,
      category: newCategory,
      videoId: newCategory === 'Video' ? prev.videoId : null,
      documentId: newCategory === 'E-book' ? prev.documentId : null,
    }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast('La imagen no debe superar los 5MB', 'error');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showToast('Por favor selecciona un archivo de imagen', 'error');
      return;
    }

    if (formData.thumbnailPreview?.startsWith?.('blob:')) {
      URL.revokeObjectURL(formData.thumbnailPreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      thumbnail: file,
      thumbnailPreview: previewUrl,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearFormError();

    if (!formData.category || (formData.category !== 'Video' && formData.category !== 'E-book')) {
      showFormError('Elige si la publicación es Video o E-book');
      return;
    }

    if (!formData.title.trim()) {
      showFormError('El título es requerido');
      return;
    }

    if (!formData.description.trim()) {
      showFormError('La descripción es requerida');
      return;
    }

    if (formData.category === 'Video' && !formData.videoId) {
      showFormError('Debes seleccionar un video del repositorio');
      return;
    }

    if (formData.category === 'E-book' && !formData.documentId) {
      showFormError('Debes seleccionar un documento del repositorio');
      return;
    }

    if (!initialData && !formData.thumbnail) {
      showFormError('La miniatura es requerida para crear una publicación');
      return;
    }

    const normalizedLabelIds = (formData.labelIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (!normalizedLabelIds.length) {
      showFormError('Debes agregar al menos una etiqueta para publicar', {
        labels: 'Selecciona al menos una etiqueta',
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const postData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        thumbnail: formData.thumbnail,
        category: formData.category,
        teacherId: formData.teacherId || 0,
        videoId: formData.category === 'Video' ? formData.videoId || 0 : 0,
        documentId: formData.category === 'E-book' ? formData.documentId || 0 : 0,
        courseId: formData.courseId || '',
        labelIds: normalizedLabelIds,
        isPublic: Boolean(formData.isPublic),
      };

      if (initialData) {
        await postsService.updatePost(initialData.id, postData);
        clearFormError();
        showToast('Publicación actualizada exitosamente', 'success');
      } else {
        await postsService.createPost(postData);
        clearFormError();
        showToast('Publicación creada exitosamente', 'success');
      }

      if (onSuccess) {
        setTimeout(() => onSuccess(), 400);
      }
    } catch (error) {
      console.error('Error al guardar publicación:', error);
      const errorMessage = extractApiErrorMessage(error);
      const nextFieldErrors = apiErrorMentionsLabels(error)
        ? { labels: 'Selecciona al menos una etiqueta válida' }
        : {};
      showFormError(errorMessage, nextFieldErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedVideo = useMemo(
    () => videos.find((v) => String(v.id) === String(formData.videoId)),
    [videos, formData.videoId]
  );
  const selectedDocument = useMemo(
    () => documents.find((d) => String(d.id) === String(formData.documentId)),
    [documents, formData.documentId]
  );
  const selectedCourse = useMemo(
    () => courses.find((c) => String(c.id) === String(formData.courseId)),
    [courses, formData.courseId]
  );
  const selectedTeacher = useMemo(
    () => teachers.find((t) => String(t.id) === String(formData.teacherId)),
    [teachers, formData.teacherId]
  );
  const selectedLabels = useMemo(() => {
    const selected = new Set(
      (formData.labelIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))
    );
    return labels.filter((label) => selected.has(Number(label.id)));
  }, [labels, formData.labelIds]);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fff',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: BRAND,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
  };

  if (isLoadingData) {
    return (
      <Box className="flex min-h-[320px] flex-col items-center justify-center gap-4">
        <CircularProgress sx={{ color: BRAND }} />
        <Typography color="text.secondary">Preparando el editor de publicación...</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} className="space-y-5">
      {!hideTitle && (
        <Typography variant="h6" className="font-bold" sx={{ color: BRAND }}>
          {initialData ? 'Editar publicación' : 'Nueva publicación'}
        </Typography>
      )}

      {formAlert && (
        <Alert
          ref={formAlertRef}
          severity="error"
          variant="filled"
          icon={<ErrorOutline fontSize="inherit" />}
          onClose={clearFormError}
          sx={{
            borderRadius: '14px',
            alignItems: 'center',
            fontWeight: 600,
            boxShadow: '0 10px 28px rgba(185, 28, 28, 0.22)',
          }}
        >
          {formAlert}
        </Alert>
      )}

      <Box className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)]">
        <Stack spacing={2.5}>
          <Section
            step="1"
            title="Tipo de contenido"
            hint="Define cómo se mostrará la publicación en el feed"
          >
            <Box className="flex flex-col gap-3 sm:flex-row">
              <CategoryCard
                active={formData.category === 'Video'}
                icon={Videocam}
                title="Video"
                subtitle="Vincula un video del repositorio"
                onClick={() => handleCategoryChange('Video')}
              />
              <CategoryCard
                active={formData.category === 'E-book'}
                icon={MenuBook}
                title="E-book"
                subtitle="Vincula un documento o material"
                onClick={() => handleCategoryChange('E-book')}
              />
            </Box>
          </Section>

          <Section step="2" title="Contenido" hint="Título y texto que verán los usuarios">
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Título"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                placeholder="Ej. Novedades del módulo dermatológico"
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label="Descripción"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                multiline
                minRows={4}
                placeholder="Escribe un resumen atractivo para la comunidad..."
                sx={fieldSx}
              />
            </Stack>
          </Section>

          <Section
            step="3"
            title="Miniatura"
            hint={initialData ? 'Opcional al editar: sube otra imagen para reemplazarla' : 'Obligatoria al crear (máx. 5MB)'}
          >
            <Box
              className="overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50"
              sx={{ borderColor: formData.thumbnailPreview ? BRAND : undefined }}
            >
              {formData.thumbnailPreview ? (
                <Box className="relative">
                  <img
                    src={formData.thumbnailPreview}
                    alt="Vista previa de miniatura"
                    className="max-h-56 w-full object-cover"
                  />
                  <Box className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-black/50 to-transparent p-3">
                    <Button
                      variant="contained"
                      component="label"
                      size="small"
                      startIcon={<CloudUpload />}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.95)',
                        color: BRAND_DARK,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      Cambiar imagen
                      <input type="file" hidden accept="image/*" onChange={handleThumbnailChange} />
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="text"
                  component="label"
                  className="flex w-full flex-col items-center gap-2 py-10"
                  sx={{ color: BRAND, textTransform: 'none' }}
                >
                  <CloudUpload sx={{ fontSize: 36 }} />
                  <Typography className="text-sm font-semibold">Arrastra o selecciona una imagen</Typography>
                  <Typography className="text-xs text-slate-500">JPG, PNG o WEBP · hasta 5MB</Typography>
                  <input type="file" hidden accept="image/*" onChange={handleThumbnailChange} />
                </Button>
              )}
            </Box>
          </Section>

          <Section
            step="4"
            title="Material vinculado"
            hint="Elige el recurso del repositorio que abrirá la publicación"
          >
            {!formData.category && (
              <Typography className="text-sm text-slate-500">
                Primero selecciona si es Video o E-book.
              </Typography>
            )}

            {formData.category === 'Video' && (
              <Autocomplete
                options={videos}
                getOptionLabel={(option) => option.title || `Video ${option.id}`}
                value={selectedVideo || null}
                onChange={(_, value) => handleChange('videoId', value?.id || null)}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="No hay videos en el repositorio"
                renderInput={(params) => (
                  <TextField {...params} label="Buscar video" required sx={fieldSx} />
                )}
              />
            )}

            {formData.category === 'E-book' && (
              <Autocomplete
                options={documents}
                getOptionLabel={(option) => option.title || `Documento ${option.id}`}
                value={selectedDocument || null}
                onChange={(_, value) => handleChange('documentId', value?.id || null)}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="No hay documentos en el repositorio"
                renderInput={(params) => (
                  <TextField {...params} label="Buscar documento" required sx={fieldSx} />
                )}
              />
            )}
          </Section>

          <Section step="5" title="Visibilidad y metadatos" hint="Opcional, pero ayuda a clasificar y promocionar">
            <Stack spacing={2}>
              <Box
                className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3"
                sx={{ bgcolor: formData.isPublic ? 'rgba(77,171,164,0.08)' : '#f8fafc' }}
              >
                <Box className="flex items-center gap-3">
                  {formData.isPublic ? (
                    <Public sx={{ color: BRAND }} />
                  ) : (
                    <Lock sx={{ color: '#94a3b8' }} />
                  )}
                  <Box>
                    <Typography className="text-sm font-semibold text-slate-800">
                      {formData.isPublic ? 'Publicación pública' : 'Solo usuarios autenticados'}
                    </Typography>
                    <Typography className="text-xs text-slate-500">
                      {formData.isPublic
                        ? 'Visible en el feed público y banners'
                        : 'No aparecerá en listados públicos'}
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.isPublic)}
                      onChange={(e) => handleChange('isPublic', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: BRAND,
                        },
                      }}
                    />
                  }
                  label=""
                />
              </Box>

              <Autocomplete
                options={teachers}
                getOptionLabel={(option) => option.name || option.nombre || `Profesor ${option.id}`}
                value={selectedTeacher || null}
                onChange={(_, value) => handleChange('teacherId', value?.id || null)}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Profesor"
                    placeholder="Opcional"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Person sx={{ ml: 1, mr: 0.5, color: BRAND, fontSize: 20 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={fieldSx}
                  />
                )}
              />

              <Autocomplete
                options={courses}
                getOptionLabel={courseOptionLabel}
                value={selectedCourse || null}
                onChange={(_, value) => handleChange('courseId', value?.id || null)}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{ ...courseOptionSx, py: 1 }}
                  >
                    <Box
                      component="span"
                      sx={courseOptionSx}
                      dangerouslySetInnerHTML={{
                        __html: option.title || `Curso ${option.id}`,
                      }}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Curso vinculado"
                    placeholder="Opcional · permite inscripción desde el post"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <School sx={{ ml: 1, mr: 0.5, color: BRAND, fontSize: 20 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={fieldSx}
                  />
                )}
              />

              <Box ref={labelsFieldRef}>
                <Autocomplete
                  multiple
                  options={labels}
                  getOptionLabel={(option) => option.name || option.Name || ''}
                  value={selectedLabels}
                  onChange={(_, newValue) => {
                    const ids = newValue
                      .map(getLabelId)
                      .filter((id) => id != null);
                    handleChange('labelIds', ids);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    Number(getLabelId(option)) === Number(getLabelId(value))
                  }
                  filterSelectedOptions
                  noOptionsText="No se encontraron etiquetas"
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || option.Name}
                        {...getTagProps({ index })}
                        key={getLabelId(option) ?? option.name}
                        size="small"
                        icon={<LocalOffer sx={{ fontSize: '14px !important' }} />}
                        sx={{
                          bgcolor: 'rgba(77,171,164,0.12)',
                          color: BRAND_DARK,
                          '& .MuiChip-icon': { color: BRAND },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(fieldErrors.labels)}
                      label="Etiquetas *"
                      placeholder={selectedLabels.length ? '' : 'Buscar y agregar...'}
                      helperText={
                        fieldErrors.labels
                          || 'Obligatorio · facilita el filtrado en el feed de la comunidad'
                      }
                      inputProps={{
                        ...params.inputProps,
                        required: false,
                      }}
                      FormHelperTextProps={{
                        sx: fieldErrors.labels
                          ? { color: '#b91c1c', fontWeight: 700, fontSize: '0.85rem' }
                          : undefined,
                      }}
                      sx={{
                        ...fieldSx,
                        ...(fieldErrors.labels
                          ? {
                              '& .MuiOutlinedInput-root': {
                                ...fieldSx['& .MuiOutlinedInput-root'],
                                backgroundColor: '#fef2f2',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#ef4444',
                                  borderWidth: 2,
                                },
                              },
                              '& .MuiInputLabel-root': { color: '#b91c1c', fontWeight: 700 },
                            }
                          : {}),
                      }}
                    />
                  )}
                />
              </Box>
            </Stack>
          </Section>
        </Stack>

        {/* Preview tipo feed */}
        <Box className="lg:sticky lg:top-3 lg:self-start">
          <Box className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <Box className="border-b border-slate-100 bg-slate-50 px-4 py-3">
              <Typography className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Vista previa del feed
              </Typography>
            </Box>

            <Box className="p-4">
              <Box className="overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
                {formData.thumbnailPreview ? (
                  <img
                    src={formData.thumbnailPreview}
                    alt=""
                    className="h-44 w-full object-cover"
                  />
                ) : (
                  <Box className="flex h-44 items-center justify-center text-slate-400">
                    <Typography className="text-sm">Sin miniatura</Typography>
                  </Box>
                )}
              </Box>

              <Box className="mt-3 space-y-2">
                <Box className="flex flex-wrap gap-1.5">
                  {formData.category && (
                    <Chip
                      size="small"
                      icon={formData.category === 'Video' ? <Videocam /> : <MenuBook />}
                      label={formData.category}
                      sx={{ bgcolor: 'rgba(77,171,164,0.15)', color: BRAND_DARK, fontWeight: 700 }}
                    />
                  )}
                  <Chip
                    size="small"
                    icon={formData.isPublic ? <Public /> : <Lock />}
                    label={formData.isPublic ? 'Pública' : 'Privada'}
                    sx={{ bgcolor: formData.isPublic ? 'rgba(136,178,79,0.18)' : '#f1f5f9', fontWeight: 600 }}
                  />
                </Box>

                <Typography className="text-base font-bold leading-snug text-slate-900">
                  {formData.title.trim() || 'Título de la publicación'}
                </Typography>
                <Typography className="line-clamp-4 text-sm leading-relaxed text-slate-600">
                  {formData.description.trim() ||
                    'La descripción aparecerá aquí para que puedas revisar cómo se lee en el feed.'}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={0.75}>
                  <Typography className="text-xs text-slate-500">
                    Material:{' '}
                    <strong className="text-slate-700">
                      {formData.category === 'Video'
                        ? selectedVideo?.title || 'Sin video'
                        : formData.category === 'E-book'
                          ? selectedDocument?.title || 'Sin documento'
                          : '—'}
                    </strong>
                  </Typography>
                  <Typography className="text-xs text-slate-500">
                    Profesor:{' '}
                    <strong className="text-slate-700">
                      {selectedTeacher?.name || selectedTeacher?.nombre || 'Sin asignar'}
                    </strong>
                  </Typography>
                  <Typography className="text-xs text-slate-500">
                    Curso:{' '}
                    <strong className="text-slate-700">
                      {selectedCourse ? courseOptionLabel(selectedCourse) : 'Ninguno'}
                    </strong>
                  </Typography>
                </Stack>

                {selectedLabels.length > 0 && (
                  <Box className="flex flex-wrap gap-1 pt-1">
                    {selectedLabels.map((label) => (
                      <Chip
                        key={label.id}
                        size="small"
                        label={label.name || label.Name}
                        sx={{ bgcolor: 'rgba(77,171,164,0.1)', color: BRAND_DARK, height: 22 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className="sticky bottom-0 z-10 -mx-1 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white/95 px-1 py-3 backdrop-blur sm:flex-row">
        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1"
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              py: 1.25,
              borderColor: '#cbd5e1',
              color: '#334155',
              fontWeight: 600,
            }}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          className="flex-1"
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Article />}
          sx={{
            textTransform: 'none',
            borderRadius: '12px',
            py: 1.25,
            bgcolor: BRAND,
            fontWeight: 700,
            boxShadow: '0 8px 20px rgba(77,171,164,0.28)',
            '&:hover': { bgcolor: BRAND_DARK },
          }}
        >
          {isSubmitting
            ? 'Guardando...'
            : initialData
              ? 'Guardar cambios'
              : 'Publicar'}
        </Button>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === 'error' ? 8000 : 4000}
        onClose={handleCloseToast}
        anchorOrigin={{
          vertical: toast.severity === 'error' ? 'top' : 'bottom',
          horizontal: toast.severity === 'error' ? 'center' : 'right',
        }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant={toast.severity === 'error' ? 'filled' : 'standard'}
          sx={{
            width: '100%',
            maxWidth: 480,
            fontWeight: toast.severity === 'error' ? 700 : 500,
            boxShadow: toast.severity === 'error'
              ? '0 12px 32px rgba(185, 28, 28, 0.28)'
              : undefined,
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PostForm;
